import { AppointmentStatus } from '@nova/core';
import util from '@satellite/plugins/util';
import { isMobile } from '@satellite/plugins/util';
import { useAnalytics } from '@satellite/composables/useAnalytics';
import type { TrackOptions } from '@satellite/analytics/AnalyticsClient';
import {
  Events,
  type AppointmentIdCopiedPayload,
  type AppointmentStatusUpdatedPayload,
  type AppointmentsSearchedPayload,
  type HeliosEventsMap,
  type SelfCheckInAppointmentLinkedPayload,
  type SelfCheckInAppointmentUnlinkedPayload,
  type SelfCheckInCtaClickedPayload,
  type SelfCheckInRequestedPayload
} from '@/analytics/events';

export interface AppointmentStatusUpdatedParams {
  appointment: {
    id?: string;
    orgId?: string;
    start?: string;
    refNumber?: string;
    tags?: unknown;
    status?: string;
    eta?: string;
    statusTimeline?: Record<string, string>;
    dock?: { warehouse?: { id?: string; name?: string } };
  };
  /** Descriptive text for the "Update Type" property, e.g. `Status: ${statusTimeTitle}`. */
  statusChangeDescription: string;
  changedBy?: string;
  entryPoint?: string | null;
  /** Org id/name fallback used when the appointment itself doesn't carry orgId (caller resolves this from its own store access). */
  orgId?: string | null;
  orgName?: string | null;
}

export type TrackAppointmentStatusUpdatedOptions = TrackOptions<AppointmentStatusUpdatedPayload>;
export type TrackAppointmentsSearchedOptions = TrackOptions<AppointmentsSearchedPayload>;
export type TrackSelfCheckInAppointmentLinkedOptions =
  TrackOptions<SelfCheckInAppointmentLinkedPayload>;
export type TrackSelfCheckInAppointmentUnlinkedOptions =
  TrackOptions<SelfCheckInAppointmentUnlinkedPayload>;
export type TrackSelfCheckInCtaClickedOptions = TrackOptions<SelfCheckInCtaClickedPayload>;
export type TrackSelfCheckInRequestedOptions = TrackOptions<SelfCheckInRequestedPayload>;
export type TrackGateManagementRequestedOptions = TrackOptions<SelfCheckInRequestedPayload>;
export type TrackAppointmentIdCopiedOptions = TrackOptions<AppointmentIdCopiedPayload>;

export function useAppointmentAnalytics() {
  const analytics = useAnalytics<HeliosEventsMap>();

  function trackAppointmentStatusUpdated(
    params: AppointmentStatusUpdatedParams,
    options: TrackAppointmentStatusUpdatedOptions = {}
  ) {
    const { appointment, statusChangeDescription, changedBy, entryPoint, orgId, orgName } = params;

    const payload: AppointmentStatusUpdatedPayload = {
      'Org ID': appointment?.orgId || orgId,
      'Org Name': orgName,
      'Warehouse ID': appointment?.dock?.warehouse?.id,
      'Warehouse Name': appointment?.dock?.warehouse?.name,
      'Appointment ID': appointment?.id,
      'Appointment Start': appointment?.start,
      'Appointment Ref #': appointment?.refNumber,
      'Appointment Tags': appointment?.tags,
      'Dwell Time': 'N/A',
      'Arrival Time': 'N/A',
      'Current Status': appointment?.status || 'N/A',
      'Update Type': statusChangeDescription,
      'Appointment ETA': appointment?.eta,
      'Changed By': changedBy ?? 'Warehouse User',
      'Entry Point': isMobile() ? 'Mobile Details Page' : entryPoint ?? 'Details Modal',
      ...(options.additionalPayload ?? {})
    };

    if (appointment?.statusTimeline) {
      const completedTime = appointment.statusTimeline[AppointmentStatus.Completed];
      const arrivedTime = appointment.statusTimeline[AppointmentStatus.Arrived];

      if (completedTime) {
        payload['Dwell Time'] = util.getReadableDuration(
          arrivedTime,
          completedTime,
          'America/Phoenix',
          ''
        );
      }

      if (arrivedTime) {
        payload['Arrival Time'] = arrivedTime;
      }
    }

    analytics.track(Events.APPOINTMENT.STATUS_UPDATED, payload);
  }

  function trackAppointmentsSearched(
    payload: AppointmentsSearchedPayload,
    options: TrackAppointmentsSearchedOptions = {}
  ) {
    analytics.track(Events.APPOINTMENT.SEARCHED, {
      ...payload,
      ...(options.additionalPayload ?? {})
    });
  }

  function trackSelfCheckInAppointmentLinked(
    payload: SelfCheckInAppointmentLinkedPayload,
    options: TrackSelfCheckInAppointmentLinkedOptions = {}
  ) {
    analytics.track(Events.SELF_CHECK_IN.APPOINTMENT_LINKED, {
      ...payload,
      ...(options.additionalPayload ?? {})
    });
  }

  function trackSelfCheckInAppointmentUnlinked(
    payload: SelfCheckInAppointmentUnlinkedPayload,
    options: TrackSelfCheckInAppointmentUnlinkedOptions = {}
  ) {
    analytics.track(Events.SELF_CHECK_IN.APPOINTMENT_UNLINKED, {
      ...payload,
      ...(options.additionalPayload ?? {})
    });
  }

  function trackSelfCheckInCtaClicked(
    payload: SelfCheckInCtaClickedPayload,
    options: TrackSelfCheckInCtaClickedOptions = {}
  ) {
    analytics.track(Events.SELF_CHECK_IN.CTA_CLICKED, {
      ...payload,
      ...(options.additionalPayload ?? {})
    });
  }

  function trackSelfCheckInRequested(
    payload: SelfCheckInRequestedPayload,
    options: TrackSelfCheckInRequestedOptions = {}
  ) {
    analytics.track(Events.SELF_CHECK_IN.REQUESTED, {
      ...payload,
      ...(options.additionalPayload ?? {})
    });
  }

  function trackGateManagementRequested(
    payload: SelfCheckInRequestedPayload,
    options: TrackGateManagementRequestedOptions = {}
  ) {
    analytics.track(Events.SELF_CHECK_IN.GATE_MANAGEMENT_REQUESTED, {
      ...payload,
      ...(options.additionalPayload ?? {})
    });
  }

  function trackAppointmentIdCopied(
    payload: AppointmentIdCopiedPayload,
    options: TrackAppointmentIdCopiedOptions = {}
  ) {
    analytics.track(Events.APPOINTMENT.ID_COPIED, {
      ...payload,
      ...(options.additionalPayload ?? {})
    });
  }

  return {
    trackAppointmentStatusUpdated,
    trackAppointmentsSearched,
    trackSelfCheckInAppointmentLinked,
    trackSelfCheckInAppointmentUnlinked,
    trackSelfCheckInCtaClicked,
    trackSelfCheckInRequested,
    trackGateManagementRequested,
    trackAppointmentIdCopied
  };
}
